/**
 * Envelope stored under each key in `user_data.view_settings`.
 *
 * `payload` is the view's own settings shape, opaque to the envelope.
 * `baseVersion` is present only for keys
 * that have a server-side default to drift from (layouts) — it records which
 * default the customization derived from, so a changed default is detectable
 * and reconcilable.
 *
 * Pure type module: no Vue/Apollo imports, so the Pinia store and the view
 * composables can both depend on it without a cycle.
 */
export interface ViewSettingEntry {
  baseVersion?: string
  savedAt: string
  payload: unknown
}

/**
 * Narrows an unknown value to `ViewSettingEntry`. Used at the one place a
 * value crosses the network boundary into the store (the login fetch) — a
 * malformed row shouldn't propagate into every `read()` call site's `as T`
 * cast downstream.
 */
export function isViewSettingEntry(value: unknown): value is ViewSettingEntry {
  if (!value || typeof value !== "object" || !("payload" in value)) return false
  const { savedAt, baseVersion } = value as { savedAt?: unknown; baseVersion?: unknown }
  if (typeof savedAt !== "string") return false
  // baseVersion is optional, but a present-and-wrong-typed one is exactly the
  // kind of malformed row this guard exists to catch: staleness detection
  // elsewhere compares it as a string, so a non-string value wouldn't crash
  // but would silently break that comparison forever for this entry.
  return baseVersion === undefined || typeof baseVersion === "string"
}

/**
 * A list's shared default view, stored in `list.data.view_settings`. Each
 * presentation's entry is that view's persisted payload minus its personal
 * keys (scroll, expanded groups, open stacks). `savedAt` is compared with each
 * user's own entry: whichever was saved later wins — see useViewSettings.
 */
export interface ListViewSettings {
  savedAt: string
  layout?: string
  grid?: object
  gallery?: object
  kanban?: object
}

export type ListViewKey = "grid" | "gallery" | "kanban"
